<template>
  <div class="mx-3" v-cloak>
    <div class="d-flex flex-row justify-content-between">
      <!-- <div style="color:gray">&nbsp; © ORBITA-HQ 2018</div> -->
      &nbsp;
      <b-button
        v-b-toggle.collapseHelp
        @click="scrollHelpIntoView"
        variant="link"
        size="sm"
      >
        Help</b-button
      >
    </div>

    <b-collapse id="collapseHelp" class="mt-2">
      <h2 id="scrollTarget">Help</h2>

      <b-alert show variant="primary" v-if="privileges.blindedResearcher"
        >You are a blinded researcher. The help below is the outline of the
        layout seen by the last participant you accessed. If you don't currently
        have a participant open above, you can ignore any error messages about
        missing information.</b-alert
      >

      <b-alert show variant="primary" v-if="privileges.unblindedResearcher"
        >You are an <strong>unblinded</strong> researcher, so remember you only
        have access to participants before they are randomized. The help below
        is the outline of the layout seen by the last participant you accessed.
        If you don't currently have a participant open above, you can ignore any
        error messages about missing information.</b-alert
      >

      <MyAnginaIs :text="myAnginaIs"></MyAnginaIs>

      <p>
        Use this App every day throughout the trial to report the symptoms you
        had the day before. Once a week, the App will additionally ask whether
        you get angina when you
        {{
          windowsQuestions.activity_001 ||
          "** SOME IMPORTANT INFORMATION IS MISSING. PLEASE CONTACT THE ORBITA TEAM AND ASK THEM TO 'SET UP YOUR WEEKLY ACTIVITY DESCRIPTION' **"
        }}
        and when you
        {{
          windowsQuestions.activity_002 ||
          "** SOME IMPORTANT INFORMATION IS MISSING. PLEASE CONTACT THE ORBITA TEAM AND ASK THEM TO 'SET UP YOUR WEEKLY ACTIVITY DESCRIPTION' **"
        }}. The App will say when you are up to date with data entry.
      </p>

      <h4>What happens if I miss a day?</h4>
      <p>
        If you miss a day, for example from lack of internet access, you can
        enter the data later. Our team will phone you if there is too much
        delay.
      </p>

      <h4>
        Do I count episodes of angina I get when I
        {{ windowsQuestions.activity_001 }} or
        {{ windowsQuestions.activity_002 }}?
      </h4>
      <p>
        Yes, include such episodes within your daily angina counts. Then at the
        weekly question about angina during those specific activities, please
        answer yes. When we process the data, we know not to double-count the
        answers to the weekly questions as extra episodes.
      </p>

      <h4>How do I use the mild-moderate-severe slider?</h4>
      <p>
        Use the slider to record your personal opinion on the severity of the
        angina you had on the day being asked about. This is a personal
        judgement for you to make at the time you are asked. We expect the way
        you make these judgements may naturally change over the course of the
        study. We are asking your independent view on how you feel each day.
      </p>

      <h4>What happens if I make a mistake?</h4>
      <p>
        Make a note of what you intended to enter and then call our helpline.
      </p>

      <h4>Why can't I see my previous entries?</h4>
      <p>
        During the trial we need your independent answers from the separate days
        and therefore the app does not show previous entries. After you finish
        the trial, we can show you all your entries.
      </p>

      <h4>What if the app isn’t working properly?</h4>
      <p>Please call our helpline.</p>

      <h4>What should I do if I don’t feel well?</h4>
      <p>
        If you want to discuss this with us, please call our helpline at any
        time.
      </p>

      <h4>Helpline number</h4>
      <p>07885587409</p>

      <h4>Advanced Tools</h4>
      <p>
        ORBITA staff may ask you to click this button
        <b-button
          b-button
          v-b-toggle.collapseAdvanced
          size="sm"
          variant="outline-primary"
          >Advanced Tools</b-button
        >
      </p>

      <b-collapse id="collapseAdvanced" class="mt-2">
        <b-alert variant="light" show>
          <hr />

          <div
            style="width: 100%"
            class="my-2 d-flex flex-row justify-content-between"
          >
            <h5>About your device</h5>
          </div>

          <div
            style="width: 100%"
            class="my-2 d-flex flex-row justify-content-between"
          >
            <div class="half">This device thinks the GMT date and time is:</div>
            <div class="half">{{ new Date().toUTCString() }}</div>
          </div>

          <div
            style="width: 100%"
            class="my-2 d-flex flex-row justify-content-between"
          >
            <div class="half">
              The device's operating system reports the time zone as:
            </div>
            <div class="half">
              {{ Intl.DateTimeFormat().resolvedOptions().timeZone }}
            </div>
          </div>

          <div
            style="width: 100%"
            class="my-2 d-flex flex-row justify-content-between"
          >
            <div class="half">
              Based on the above, it reports the local time to be:
            </div>
            <div class="half">{{ new Date().toLocaleString() }}</div>
          </div>

          <hr />

          <div
            style="width: 100%"
            class="my-2 d-flex flex-row justify-content-between"
          >
            <h5>About the App</h5>
          </div>

          <div
            style="width: 100%"
            class="my-2 d-flex flex-row justify-content-between"
          >
            <div class="half">App on your device was built at:</div>
            <div class="half">{{ buildVersion }}</div>
          </div>

          <div
            style="width: 100%"
            class="my-2 d-flex flex-row justify-content-between"
          >
            <div class="half">Your unique user ID:</div>
            <div class="half">{{ userId }}</div>
          </div>

          <div
            style="width: 100%"
            class="my-2 d-flex flex-row justify-content-between"
          >
            <div class="half">If asked to log out of the App:</div>
            <div class="half">
              <b-button variant="outline-dark" @click="$emit('trySignOut')"
                >Log out</b-button
              >
            </div>
          </div>

          <hr />
          <br />
          <b-button
            b-button
            v-b-toggle.collapseAdvanced
            size="sm"
            variant="outline-primary"
          >
            Close Advanced Tools
          </b-button>

          <br />
        </b-alert>
      </b-collapse>

      <div
        style="width: 100%"
        class="my-2 d-flex flex-row justify-content-between"
      >
        <div>&nbsp;</div>
        <b-button block v-b-toggle.collapseHelp variant="primary"
          >Close Help</b-button
        >
      </div>
    </b-collapse>
  </div>
</template>

<script>
import { BUILDVERSION } from "./from_st_elsewhere/BuildVersion.js"; // This is overwritten by the building process
import MyAnginaIs from "./MyAnginaIs.vue";
import { getNewest } from "./utilGetNewest.js"; // but remember to also re-export it in methods
export default {
  components: {
    MyAnginaIs,
  },

  props: {
    myAnginaIs: String, // is from participant.contactObj.myAnginaIs
    windowsQuestions: {
      // is from participant.contactObj.windows.questions
      activity_001: String,
      activity_002: String,
    },
    userId: String,
    privileges: {},
  },

  data: () => ({
    buildVersion: BUILDVERSION, // A constant written to buildVersion.js by the build command
    doc: document,
  }),

  methods: {
    getNewest, // It was imported into the global area, and then I have to re-export it here so the HTML Template can see it. Basically I create a this.getNewest which is identical to the global getNewest
    scrollHelpIntoView: function () {
      setTimeout(
        () =>
          document
            .getElementById("scrollTarget")
            .scrollIntoView({ behaviour: "smooth" }),
        500
      );
    },
  },
};
</script>

<style>
.half {
  width: 50%;
  overflow-wrap: break-word;
  margin-right: 1em;
}
</style>



// WEBPACK FOOTER //
// src/components/AppHelp.vue